import { Serializable } from '@/models/core/serializable';
import { GEnum } from '@/constants/enum.defination';
import { GradeSchoolClass, SchoolClass } from '@/models/school-class/school-class';
import { Student } from '@/models/student/student';
import { Activity } from '@/models/activity/activity';
import { Event, EventGiftCcourse } from '@/models/jgrow/event';
import { Survey } from '@/models/jgrow/survey';
import { JgrowEventLikertScale } from '@/models/jgrow/jgrow-event-likert-scale';

export class EventParam {
  title: string = ''; // 受検コース名
  destination: number = 1; // 行き先
  startDate: Date = new Date(); // イベント開始日
  endDate: Date = new Date(); // イベント終了日
  beforeSurveyStartDate: Date = new Date(); // 事前アンケート開始日
  beforeSurveyEndDate: Date = new Date(); // 事前アンケート終了日
  afterSurveyStartDate: Date = new Date(); // 事後アンケート開始日
  afterSurveyEndDate: Date = new Date(); // 事後アンケート終了日
  beforeExam: boolean = false; // 事前受検:実施 する・しない
  beforeExamStartDate: Date = new Date(); // 事前受検開始日
  beforeExamEndDate: Date = new Date(); // 事前受検終了日
  afterExam: boolean = false; // 事後受検:実施 する・しない
  afterExamStartDate: Date = new Date(); // 事後受検開始日
  afterExamEndDate: Date = new Date(); // 事後受検終了日
  questionSettingType: number = -1; // 設問タイプ
  competencySettingType: GEnum.JgrowCompetencySettingType = GEnum.JgrowCompetencySettingType.Basic; // 設問数選択
  competencyIDs: number[] = []; // 受検コースに設定したコンピテンシーID一覧
  q1: string = '';
  q1Enable: boolean = false;
  q2: string = '';
  q2Enable: boolean = false;
  q3: string = '';
  q3Enable: boolean = false;
  selectedActivities: Activity[] = [];
  activities: number[] = [];
  schoolClasses: SchoolClass[] = []; // 受検対象者クラス一覧
  students: Student[] = []; // 受検対象者生徒一覧
  gradeSchoolClasses: GradeSchoolClass[]; // 学年別クラス一覧
  status: number = 0; // ステータス
  likertScale: JgrowEventLikertScale[] = [];

  // クラス別、または、学年別の生徒人数(key:クラスID or 学年,value:人数)
  studentCountByDomain(domainType: GEnum.TargetDomainType) {
    return this.students.reduce((result: Map<number, number>, student: Student) => {
      let key: number = 0;
      switch (domainType) {
        case GEnum.TargetDomainType.SchoolClass:
          key = student.schoolClass.schoolClassID;
          break;
        case GEnum.TargetDomainType.Grade:
          key = student.schoolClass.grade;
          break;
      }
      if (!result.has(key)) {
        result.set(key, 0);
      }
      const count: number = result.get(key) + 1;
      result.set(key, count);
      return result;
    }, new Map<number, number>());
  }

  get getSelectedActivities(): Activity[] {
    return this.selectedActivities.filter((act: Activity) => act.isSelected === true);
  }

  get hasLikertScales(): boolean {
    return this.likertScale.length > 0;
  }

  // モデル初期化
  clean() {
    this.title = '';
    this.destination = 1;
    this.startDate = null;
    this.endDate = null;
    this.beforeSurveyStartDate = null;
    this.beforeSurveyEndDate = null;
    this.afterSurveyStartDate = null;
    this.afterSurveyEndDate = null;
    this.beforeExam = false;
    this.beforeExamStartDate = null;
    this.beforeExamEndDate = null;
    this.afterExam = false;
    this.afterExamStartDate = null;
    this.afterExamEndDate = null;
    this.competencySettingType = GEnum.JgrowCompetencySettingType.Basic;
    this.competencyIDs = []; // 受検コースに設定したコンピテンシーID一覧
    this.q1 = '';
    this.q1Enable = false;
    this.q2 = '';
    this.q2Enable = false;
    this.q3 = '';
    this.q3Enable = false;
    this.selectedActivities = [];
    this.activities = [];
    this.status = 0;
    this.students = [];
    this.schoolClasses = [];
    this.likertScale = [];
  }

  convertGradeSchoolClasses(inputSchoolClasses: any[]): GradeSchoolClass[] {
    let schoolClassByGrade: any = new Map();

    inputSchoolClasses.forEach((value: any) => {
      if (!schoolClassByGrade.has(value.grade)) {
        schoolClassByGrade.set(value.grade, []);
      }
      schoolClassByGrade.get(value.grade).push(value);
    });

    const grades = [...schoolClassByGrade.keys()].sort();
    const result: GradeSchoolClass[] = grades.map((grade: number) => {
      return new GradeSchoolClass().deserialize({ grade, schoolClasses: schoolClassByGrade.get(grade) });
    });

    return result;
  }

  get isBeforeStart(): boolean {
    return this.status === GEnum.ExamStatus.BeforeStart;
  }

  get isUnderExamination(): boolean {
    return this.status === GEnum.ExamStatus.UnderExamination;
  }

  get isBeforeEnd(): boolean {
    return this.status === GEnum.ExamStatus.BeforeStart || this.status === GEnum.ExamStatus.UnderExamination;
  }

  convertFromEvent(data: Event): EventParam {
    this.title = data.eventName;
    this.destination = data.destination;
    this.startDate = data.startDate;
    this.endDate = data.endDate;
    this.beforeSurveyStartDate = data.surveys.find((suv: Survey) => suv.type === 1).startDate;
    this.beforeSurveyEndDate = data.surveys.find((suv: Survey) => suv.type === 1).endDate;
    this.afterSurveyStartDate = data.surveys.find((suv: Survey) => suv.type === 2).startDate;
    this.afterSurveyEndDate = data.surveys.find((suv: Survey) => suv.type === 2).endDate;
     // 受検コースに設定したコンピテンシーID一覧
    this.q1 = data.q1;
    this.q1Enable = data.q1Enable;
    this.q2 = data.q2;
    this.q2Enable = data.q2Enable;
    this.q3 = data.q3;
    this.q3Enable = data.q3Enable;
    this.likertScale = data.likertScale;
    this.competencyIDs = data.competencies;
    //this.selectedActivities = data.activities.map;
    this.students = [];
    this.schoolClasses = [];
    // 受検が設定されている場合
    if (data.giftCourses.length > 0) {
      // 事前測定（受検）が存在する
      const beforeExam = data.giftCourses.find((exm: EventGiftCcourse) => exm.type === 1);
      if (beforeExam) {
        this.beforeExam = true;
        this.beforeExamStartDate = beforeExam.startTime;
        this.beforeExamEndDate = beforeExam.deadline;
      }

      // 事後測定（受検）が存在する
      const afterExam = data.giftCourses.find((exm: EventGiftCcourse) => exm.type === 2);
      if (afterExam) {
        this.afterExam = true;
        this.afterExamStartDate = afterExam.startTime;
        this.afterExamEndDate = afterExam.deadline;
      }
    }
    return this;
  }

  validationLikertScales(): boolean {
    const falseResult = this.likertScale.filter((ls: JgrowEventLikertScale) => ls.doValidation() === false);
    return falseResult.length < 1;
  }
}

export class EventCreateRequest {
  eventName: string;
  startDate: string;
  endDate: string;
  destination: number;
  q1Enable: boolean;
  q1: string;
  q2Enable: boolean;
  q2: string;
  q3Enable: boolean;
  q3: string;
  likertScales: JgrowEventLikertScale[];
  activities: number[];
  competencies: number[];
  schoolClassIDs: number[];
  userIDs: number[];
  beforeSurveyStartDate: string;
  beforeSurveyEndDate: string;
  afterSurveyStartDate: string;
  afterSurveyEndDate: string;
  beforeExam: boolean;
  beforeExamStartDate: string;
  beforeExamEndDate: string;
  afterExam: boolean;
  afterExamStartDate: string;
  afterExamEndDate: string;
}
